  import React, { useEffect, useState, useMemo } from "react";
  import { useParams, useNavigate } from "react-router-dom";
  import { ContenedorPageTemplate } from "../../../components/templates/ContenedorPageTemplate";
  import { showAlertAsistencia, showErrorAlert } from "../../../components/molecules/ShowAlert";
  import {
    getAsistenciasByDocenteIdiomaGrupoEstado,
    getAllTipoAsistencia,
    registrarAsistencia,
    actualizarAsistencia,
  } from "../../../services/asistencia.service";
  import { getAllRegistrosV2ByIdDocenteIdIdiomaIdGrupoEstado } from "../../../services/registroV2.service";
  import { exportarAsistenciaGrupo } from "../../../services/grupo.service";

  function AsistenciaPage() {
    const getRegistroId = (reg) => reg.idRegistroNotasV2 || reg.idRegistro || reg.IdRegistroNotasV2 || reg.IdRegistro;

    const { idDocente, idIdioma, idGrupo } = useParams();
    const estado = "Progreso";

    const [estudiantes, setEstudiantes] = useState(null);
    const [asistenciasBD, setAsistenciasBD] = useState([]);
    const [tiposAsistencia, setTiposAsistencia] = useState([]);
    const [seleccion, setSeleccion] = useState({}); // Ahora guardará {idRegistro-fecha: idTipoAsistencia}
    const [fechaSeleccionada, setFechaSeleccionada] = useState(new Date().toISOString().split("T")[0]); // Fecha para agregar nueva asistencia
    const [fechasAgregadas, setFechasAgregadas] = useState([]); // Fechas que el usuario ha agregado manualmente
    const [mostrarTodasLasFechas, setMostrarTodasLasFechas] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();

    // Función para generar la clave única de localStorage
    const generarClaveLocal = (idDocente, idGrupo, idIdioma, fecha) =>
      `asistenciaLocal_${idDocente}_${idGrupo}_${idIdioma}_${fecha}`;

    // Función para ordenar estudiantes alfabéticamente por nombre
    const ordenarEstudiantesAlfabeticamente = (estudiantes) => {
      return [...estudiantes].sort((a, b) => {
        const nombreA = (a.estudiante?.nombre || '').trim();
        const nombreB = (b.estudiante?.nombre || '').trim();
        const apellidoA = (a.estudiante?.apellido || '').trim();
        const apellidoB = (b.estudiante?.apellido || '').trim();
        
        // Comparar nombres primero usando localeCompare para español
        const compareNombre = nombreA.localeCompare(nombreB, 'es', { sensitivity: 'base' });
        if (compareNombre !== 0) return compareNombre;
        
        // Si los nombres son iguales, comparar apellidos
        return apellidoA.localeCompare(apellidoB, 'es', { sensitivity: 'base' });
      });
    };

    // Obtener todas las fechas únicas de asistencia ordenadas
    const fechasUnicas = useMemo(() => {
      const fechas = new Set();
      
      // Primero agregar fechas de la BD
      if (asistenciasBD && asistenciasBD.length > 0) {
        asistenciasBD.forEach((a) => {
          if (a.fechaAsistencia) {
            const fecha = a.fechaAsistencia.split("T")[0];
            fechas.add(fecha);
          }
        });
      }
      
      // Agregar fechas que el usuario ha agregado manualmente
      fechasAgregadas.forEach((fecha) => {
        fechas.add(fecha);
      });
      
      // Siempre incluir la fecha seleccionada actual si no está vacía
      if (fechaSeleccionada) {
        fechas.add(fechaSeleccionada);
      }
      
      // Si no hay ninguna fecha, al menos mostrar la fecha actual
      if (fechas.size === 0 && fechaSeleccionada) {
        fechas.add(fechaSeleccionada);
      }
      
      return Array.from(fechas).sort((a, b) => new Date(a) - new Date(b));
    }, [asistenciasBD, fechaSeleccionada, fechasAgregadas]);

    // Encontrar el tipo de asistencia "Presente"
    const tipoPresente = useMemo(() => {
      return tiposAsistencia.find(t => 
        t.nombre?.toLowerCase().includes('presente') || 
        t.sigla?.toUpperCase() === 'P'
      );
    }, [tiposAsistencia]);

    const obtenerMensajeError = (err, fallback) => (
      err?.response?.data?.message ||
      err?.response?.data?.errors?.[0] ||
      err?.response?.data?.title ||
      err?.message ||
      fallback
    );

    // Función para obtener asistencia de un estudiante en una fecha específica
    const obtenerAsistenciaEstudiante = (idRegistro, fecha) => {
      const asistencia = asistenciasBD.find(
        (a) =>
          (a.idRegistroNotasV2 || a.idRegistro || a.IdRegistroNotasV2 || a.IdRegistro) === idRegistro &&
          a.fechaAsistencia?.split("T")[0] === fecha
      );
      return asistencia?.idTipoAsistencia || null;
    };

    // Función para obtener la sigla del tipo de asistencia
    const obtenerSiglaAsistencia = (idTipoAsistencia) => {
      if (!idTipoAsistencia) return "";
      const tipo = tiposAsistencia.find((t) => t.idTipoAsistencia === idTipoAsistencia);
      return tipo?.sigla || "";
    };

    // Función para verificar si un tipo de asistencia cuenta como asistencia (P, L, R)
    const cuentaComoAsistencia = (idTipoAsistencia) => {
      if (!idTipoAsistencia) return false;
      
      const tipo = tiposAsistencia.find((t) => t.idTipoAsistencia === idTipoAsistencia);
      if (!tipo) return false;
      
      const sigla = (tipo.sigla || "").trim().toUpperCase();
      // Solo P, L y R cuentan como asistencia
      return sigla === "P" || sigla === "L" || sigla === "R";
    };

    // Función para calcular frecuencia (porcentaje de asistencia)
    const calcularFrecuencia = (idRegistro) => {
      if (!fechasUnicas || fechasUnicas.length === 0) return { total: 0, porcentaje: "0,0" };
      
      let asistencias = 0;
      let totalFechas = fechasUnicas.length;
      
      fechasUnicas.forEach((fecha) => {
        // Primero verificar selección actual, luego BD
        const clave = `${idRegistro}-${fecha}`;
        let idTipo = seleccion[clave];
        
        if (!idTipo) {
          idTipo = obtenerAsistenciaEstudiante(idRegistro, fecha);
        }
        
        // Solo contar si cuenta como asistencia (P, L, R)
        if (idTipo && cuentaComoAsistencia(idTipo)) {
          asistencias++;
        }
      });
      
      if (totalFechas === 0) return { total: 0, porcentaje: "0,0" };
      const porcentaje = (asistencias / totalFechas) * 100;
      return { 
        total: asistencias, 
        porcentaje: porcentaje.toFixed(1).replace('.', ',') 
      };
    };

    // Función para obtener porcentaje general de asistencia del grupo
    const porcentajeGeneralAsistencia = useMemo(() => {
      if (!estudiantes || estudiantes.length === 0 || !fechasUnicas || fechasUnicas.length === 0) return null;
      
      let totalAsistencias = 0;
      let totalPosibles = estudiantes.length * fechasUnicas.length;
      
      estudiantes.forEach((reg) => {
        const regId = getRegistroId(reg);
        fechasUnicas.forEach((fecha) => {
          // Primero verificar selección actual, luego BD
          const clave = `${regId}-${fecha}`;
          let idTipo = seleccion[clave];
          
          if (!idTipo) {
            idTipo = obtenerAsistenciaEstudiante(regId, fecha);
          }
          
          // Solo contar si cuenta como asistencia (P, L, R)
          if (idTipo && cuentaComoAsistencia(idTipo)) {
            totalAsistencias++;
          }
        });
      });
      
      if (totalPosibles === 0) return null;
      const porcentaje = (totalAsistencias / totalPosibles) * 100;
      return porcentaje.toFixed(1);
    }, [estudiantes, fechasUnicas, asistenciasBD, seleccion, tiposAsistencia]);

    // Cargar datos y asistencia
    useEffect(() => {
      if (!idDocente || !idGrupo || !idIdioma) return;

      setIsLoading(true);

      Promise.all([
        getAllRegistrosV2ByIdDocenteIdIdiomaIdGrupoEstado(idDocente, idIdioma, idGrupo, estado)
          .then((data) => {
            const estudiantesOrdenados = ordenarEstudiantesAlfabeticamente(data);
            setEstudiantes(estudiantesOrdenados);
          })
          .catch((err) => {
            showErrorAlert({ text: "Error al cargar estudiantes", icon: "error" });
            setEstudiantes([]);
          }),
        getAsistenciasByDocenteIdiomaGrupoEstado(idDocente, idIdioma, idGrupo, estado)
          .then(setAsistenciasBD)
          .catch((err) => {
            setAsistenciasBD([]);
            showErrorAlert({ text: "Error al cargar asistencias", icon: "error" });
          }),
        getAllTipoAsistencia()
          .then(setTiposAsistencia)
          .catch((err) => showErrorAlert({ text: "Error al cargar tipos de asistencia", icon: "error" }))
      ]).finally(() => {
        setIsLoading(false);
      });

      // Cargar fechas agregadas manualmente desde localStorage
      const fechasManualesGuardadas = localStorage.getItem(`fechas_manuales_${idGrupo}`);
      if (fechasManualesGuardadas) {
        try {
          setFechasAgregadas(JSON.parse(fechasManualesGuardadas));
        } catch (e) {
          console.error("Error parsing fechas manuales", e);
        }
      }
    }, [idDocente, idIdioma, idGrupo]);

    // Manejar cambios en la selección de asistencia para una fecha específica
    const handleSelectChange = (idRegistro, fecha, valor) => {
      const clave = `${idRegistro}-${fecha}`;
      const nuevoValor = valor === "" ? null : valor;
      setSeleccion((prev) => {
        const nuevo = { ...prev };
        if (nuevoValor) {
          nuevo[clave] = nuevoValor;
        } else {
          delete nuevo[clave];
        }
        return nuevo;
      });
    };

    // Obtener el valor seleccionado para un estudiante en una fecha
    const obtenerValorSeleccionado = (idRegistro, fecha) => {
      const clave = `${idRegistro}-${fecha}`;
      // Priorizar selección actual, luego buscar en BD
      if (seleccion[clave]) {
        return seleccion[clave];
      }
      const asistenciaBD = obtenerAsistenciaEstudiante(idRegistro, fecha);
      return asistenciaBD || "";
    };

    const handleGuardar = async () => {
      try {
        const cambiosPendientes = [];
        const asistenciaParaLocal = {};

        // Procesar todas las selecciones usando fechasUnicas
        const fechasParaGuardar = fechasUnicas.length > 0 ? fechasUnicas : [fechaSeleccionada].filter(Boolean);
        
        for (const reg of estudiantes) {
          for (const fecha of fechasParaGuardar) {
            if (!fecha) continue;
            
            const regId = getRegistroId(reg);
            const clave = `${regId}-${fecha}`;
            const valorSeleccionado = seleccion[clave];
            
            // Solo procesar si hay un valor seleccionado
            if (valorSeleccionado) {
              const asistenciaExistente = asistenciasBD.find(
                (a) =>
                  (a.idRegistroNotasV2 || a.idRegistro || a.IdRegistroNotasV2 || a.IdRegistro) === regId &&
                  a.fechaAsistencia?.split("T")[0] === fecha
              );

              const body = {
                // Enviar la fecha a mediodía UTC para evitar desfases de zona horaria al guardar
                fechaAsistencia: `${fecha}T12:00:00Z`,
                idTipoAsistencia: valorSeleccionado,
                idRegistroNotasV2: regId,
              };

              cambiosPendientes.push({
                asistenciaExistente,
                body,
                fecha,
                idRegistroNotasV2: regId,
              });

              // Guardar en objeto local por fecha
              if (!asistenciaParaLocal[fecha]) {
                asistenciaParaLocal[fecha] = [];
              }
              asistenciaParaLocal[fecha].push({
                idRegistroNotasV2: regId,
                idTipoAsistencia: valorSeleccionado,
                expira: Date.now() + 1000 * 60 * 60 * 24 * 30, // 1 mes
              });
            }
          }
        }

        // Ejecutar todos los cambios
        for (const cambio of cambiosPendientes) {
          if (cambio.asistenciaExistente) {
            await actualizarAsistencia(cambio.asistenciaExistente.idAsistencia, cambio.body);
          } else {
            await registrarAsistencia(cambio.body);
          }
        }

        // Guardar en localStorage por fecha
        Object.keys(asistenciaParaLocal).forEach((fecha) => {
          const clave = generarClaveLocal(idDocente, idGrupo, idIdioma, fecha);
          localStorage.setItem(clave, JSON.stringify(asistenciaParaLocal[fecha]));
        });

        showAlertAsistencia({
          text: "Asistencia guardada correctamente.",
          icon: "success",
        });

        const nuevasAsistencias = await getAsistenciasByDocenteIdiomaGrupoEstado(idDocente, idIdioma, idGrupo, estado);
        setAsistenciasBD(nuevasAsistencias);
        setSeleccion({}); // Limpiar selección después de guardar

      } catch (err) {
        showErrorAlert({
          text: `Error al guardar: ${obtenerMensajeError(err, "No se pudo guardar la asistencia.")}`,
          icon: "error"
        });
      }
    };

    const handleExportar = async () => {
      try {
        await exportarAsistenciaGrupo(idGrupo);
      } catch (err) {
        showErrorAlert({ text: `Error al exportar: ${err.message}`, icon: "error" });
      }
    };

    // Función para agregar una nueva fecha a la vista
    const handleAgregarFecha = () => {
      if (fechaSeleccionada) {
        // Agregar la fecha a la lista de fechas agregadas manualmente
        setFechasAgregadas((prev) => {
          if (!prev.includes(fechaSeleccionada)) {
            const nuevas = [...prev, fechaSeleccionada];
            // Guardar en localStorage para que no se borren al recargar si no hay asistencias aún
            localStorage.setItem(`fechas_manuales_${idGrupo}`, JSON.stringify(nuevas));
            return nuevas;
          }
          return prev;
        });
      }
    };

    // Función para marcar todos como presentes en una fecha específica
    const handleMarcarTodosPresentes = (fecha) => {
      if (!tipoPresente || !estudiantes) return;
      
      const nuevasSelecciones = { ...seleccion };
      estudiantes.forEach((reg) => {
        const regId = getRegistroId(reg);
        const clave = `${regId}-${fecha}`;
        // Solo actualizar si no tiene un valor ya seleccionado
        if (!nuevasSelecciones[clave] && !obtenerAsistenciaEstudiante(regId, fecha)) {
          nuevasSelecciones[clave] = tipoPresente.idTipoAsistencia;
        }
      });
      
      setSeleccion(nuevasSelecciones);
    };

    // Función para formatear fecha corta (1/1, 1/2, etc.)
    const formatearFechaCorta = (fechaStr) => {
      if (!fechaStr) return "";
      // Usar T00:00:00 para asegurar que se trate como hora local y evitar desfases de zona horaria
      const fecha = new Date(`${fechaStr}T00:00:00`);
      const dia = fecha.getDate();
      const mes = fecha.getMonth() + 1;
      return `${dia}/${mes}`;
    };

    const formatearFechaLarga = (fechaStr) => {
      if (!fechaStr) return "";
      const fecha = new Date(`${fechaStr}T00:00:00`);
      return fecha.toLocaleDateString("es-BO", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    };

    const getColorPorcentaje = (porcentaje) => {
      const p = parseFloat(porcentaje.replace(',', '.'));
      if (p >= 80) return "bg-green-100 text-green-700 border-green-200";
      if (p >= 60) return "bg-yellow-100 text-yellow-700 border-yellow-200";
      return "bg-red-100 text-red-700 border-red-200";
    };

    const fechasParaMostrar = useMemo(() => {
      if (mostrarTodasLasFechas) {
        return fechasUnicas;
      }
      return fechaSeleccionada ? [fechaSeleccionada] : (fechasUnicas.length > 0 ? [fechasUnicas[fechasUnicas.length - 1]] : []);
    }, [fechasUnicas, fechaSeleccionada, mostrarTodasLasFechas]);

    const numColumnasAsistencia = fechasParaMostrar.length || 1;

    return (
      <ContenedorPageTemplate fondo={false} objetos={estudiantes}>
        <div className="bg-white shadow-lg rounded-xl p-4 md:p-6 mx-auto mt-4 border border-blue-100">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate(-1)}
                className="bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300 rounded-lg py-1.5 px-3 flex items-center gap-1 font-medium transition-colors"
                title="Volver atrás"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Volver
              </button>
              <h2 className="text-2xl font-bold text-blue-900">Registro de Asistencia</h2>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleExportar}
                className="bg-blue-600 text-white font-semibold py-2 px-4 rounded-lg hover:bg-blue-700 flex items-center gap-2"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
                Exportar
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
              <p className="text-xs text-blue-700">Estudiantes en lista</p>
              <p className="text-xl font-bold text-blue-900">{estudiantes?.length || 0}</p>
            </div>
            <div className="bg-green-50 border border-green-100 rounded-lg p-3">
              <p className="text-xs text-green-700">Fechas registradas</p>
              <p className="text-xl font-bold text-green-900">{fechasUnicas.length}</p>
            </div>
            <div className="bg-purple-50 border border-purple-100 rounded-lg p-3">
              <p className="text-xs text-purple-700">Asistencia general</p>
              <p className="text-xl font-bold text-purple-900">{porcentajeGeneralAsistencia ? `${porcentajeGeneralAsistencia}%` : "--"}</p>
            </div>
          </div>

          {/* Selector de fecha y botones de acción */}
          <div className="mb-4 rounded-lg bg-gray-50 p-3">
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <label className="text-sm font-semibold text-gray-700">Fecha:</label>
                <input
                  type="date"
                  className="border border-gray-300 rounded px-3 py-1 text-sm"
                  value={fechaSeleccionada}
                  onChange={(e) => setFechaSeleccionada(e.target.value)}
                />
              </div>
              <button
                onClick={handleAgregarFecha}
                className="bg-green-600 text-white font-semibold py-1 px-3 rounded hover:bg-green-700 text-sm"
              >
                Agregar Fecha
              </button>
              <button
                onClick={() => setMostrarTodasLasFechas((prev) => !prev)}
                className={`text-sm font-semibold py-1.5 px-4 rounded-lg transition-all ${
                  mostrarTodasLasFechas 
                    ? "bg-slate-700 text-white shadow-inner" 
                    : "bg-white text-slate-700 border border-slate-300 hover:bg-slate-50 shadow-sm"
                }`}
              >
                {mostrarTodasLasFechas ? "Ver solo seleccionada" : `Ver histórico (${fechasUnicas.length} fechas)`}
              </button>
              {tipoPresente && fechaSeleccionada && (
                <button
                  onClick={() => handleMarcarTodosPresentes(fechaSeleccionada)}
                  className="bg-blue-500 text-white font-semibold py-1 px-3 rounded hover:bg-blue-600 text-sm"
                >
                  Marcar Todos Presentes ({fechaSeleccionada.split('-')[2]}/{fechaSeleccionada.split('-')[1]})
                </button>
              )}
            </div>
            {fechasUnicas.length > 0 && (
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <span className="text-sm font-semibold text-gray-700">Fechas tomadas:</span>
                <div className="flex flex-wrap gap-2">
                  {fechasUnicas.map((fecha) => {
                    const esActiva = fecha === fechaSeleccionada;
                    return (
                      <button
                        key={fecha}
                        type="button"
                        onClick={() => setFechaSeleccionada(fecha)}
                        className={`group relative flex items-center gap-1.5 rounded-xl border px-3 py-1.5 text-xs font-semibold transition-all duration-200 ${
                          esActiva
                            ? "border-blue-500 bg-blue-600 text-white shadow-md shadow-blue-100 ring-2 ring-blue-100"
                            : "border-gray-200 bg-white text-gray-600 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700"
                        }`}
                        title={`Ver asistencia del ${formatearFechaLarga(fecha)}`}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className={`h-3.5 w-3.5 ${esActiva ? "text-blue-100" : "text-gray-400 group-hover:text-blue-500"}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        {formatearFechaLarga(fecha)}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <div className="overflow-x-auto rounded-lg border border-gray-200 custom-scrollbar shadow-inner bg-slate-50/30">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-200 border-t-blue-600 mb-3"></div>
                <p className="text-blue-800 font-medium animate-pulse">Cargando lista de estudiantes...</p>
              </div>
            ) : (
              <table className="w-full border-collapse text-sm bg-white" style={{ minWidth: "900px" }}>
                <thead>
                  <tr>
                  <th
                    className="bg-blue-800 text-white text-left border border-gray-300 px-3 py-3 font-semibold sticky left-0 z-20"
                    style={{ minWidth: "220px" }}
                  >
                    Estudiante
                  </th>
                  {fechasParaMostrar.map((fecha, idx) => (
                    <th
                      key={idx}
                      className="bg-blue-800 text-white text-center border-x border-blue-700/50 px-1 py-2 font-bold"
                      style={{ minWidth: "75px" }}
                    >
                      <div className="flex flex-col items-center gap-2">
                        <span className="text-[10px] tracking-tighter bg-white/10 px-1.5 py-0.5 rounded-full">{formatearFechaCorta(fecha)}</span>
                        {tipoPresente && (
                          <button
                            onClick={() => handleMarcarTodosPresentes(fecha)}
                            className="text-[9px] font-bold bg-white/10 hover:bg-white text-white hover:text-blue-800 px-1.5 py-0 rounded transition-all border border-white/20"
                            title="Marcar todos P"
                          >
                            P
                          </button>
                        )}
                      </div>
                    </th>
                  ))}
                  <th className="bg-blue-800 text-white text-center border-x border-blue-700 px-2 py-4 font-bold" style={{ minWidth: "80px" }}>
                    Total
                  </th>
                  <th className="bg-blue-800 text-white text-center border-x border-blue-700 px-2 py-4 font-bold" style={{ minWidth: "80px" }}>
                    %
                  </th>
                  <th className="bg-blue-800 text-white text-center border-x border-blue-700 px-2 py-4 font-bold" style={{ minWidth: "110px" }}>
                    Estado
                  </th>
                </tr>
              </thead>

              {/* Filas de estudiantes */}
              <tbody>
                {estudiantes && estudiantes.length > 0 ? (
                  ordenarEstudiantesAlfabeticamente(estudiantes).map((reg) => {
                    const est = reg.estudiante;
                    const idReg = getRegistroId(reg);
                    const frecuencia = calcularFrecuencia(idReg);
                    const nombreCompleto = `${est?.nombre || ""} ${est?.apellido || ""}`.trim();

                    return (
                      <tr key={idReg} className="hover:bg-blue-50/40 transition-colors">
                        {/* Columna de nombre del estudiante */}
                        <td className="border border-gray-300 px-3 py-2 text-left font-medium sticky left-0 bg-white z-10">
                          {nombreCompleto || "-"}
                        </td>
                        
                        {/* Columnas de asistencia por fecha */}
                        {fechasParaMostrar.map((fecha, idx) => {
                          const valorSeleccionado = obtenerValorSeleccionado(idReg, fecha);
                          // Ordenar tipos de asistencia: Presente primero, luego los demás
                          const tiposOrdenados = [...tiposAsistencia].sort((a, b) => {
                            const aEsPresente = a.idTipoAsistencia === tipoPresente?.idTipoAsistencia;
                            const bEsPresente = b.idTipoAsistencia === tipoPresente?.idTipoAsistencia;
                            if (aEsPresente && !bEsPresente) return -1;
                            if (!aEsPresente && bEsPresente) return 1;
                            return 0;
                          });

                          return (
                            <td 
                              key={idx} 
                              className="border border-gray-300 px-1 py-1 text-center"
                              style={{ minWidth: "120px" }}
                            >
                              <select
                                value={valorSeleccionado || ""}
                                onChange={(e) => {
                                  handleSelectChange(idReg, fecha, e.target.value);
                                }}
                                className="w-full text-[11px] border border-gray-200 bg-white rounded px-1 py-0.5 cursor-pointer hover:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-200"
                                style={{ 
                                  minWidth: "65px",
                                  height: "24px"
                                }}
                                disabled={!tiposAsistencia || tiposAsistencia.length === 0}
                              >
                                <option value="">-- Seleccionar --</option>
                                {tiposOrdenados && tiposOrdenados.length > 0 ? (
                                  tiposOrdenados.map((tipo) => (
                                    <option 
                                      key={tipo.idTipoAsistencia} 
                                      value={tipo.idTipoAsistencia}
                                      style={tipo.idTipoAsistencia === tipoPresente?.idTipoAsistencia ? { fontWeight: 'bold' } : {}}
                                    >
                                      {tipo.sigla ? `${tipo.sigla}: ${tipo.nombre}` : tipo.nombre}
                                    </option>
                                  ))
                                ) : (
                                  <option value="" disabled>Cargando tipos...</option>
                                )}
                              </select>
                            </td>
                          );
                        })}
                        
                        {/* Columnas de frecuencia */}
                        <td className="border border-gray-100 px-2 py-2 text-center bg-gray-50/50 font-bold text-slate-700" style={{ minWidth: "70px" }}>
                          {frecuencia?.total ?? "0"}
                        </td>
                        <td className="border border-gray-100 px-2 py-2 text-center bg-gray-50/50" style={{ minWidth: "70px" }}>
                          <span className={`inline-block px-2 py-0.5 rounded-md text-xs font-bold border ${getColorPorcentaje(frecuencia?.porcentaje || "0,0")}`}>
                            {frecuencia?.porcentaje ?? "0,0"}%
                          </span>
                        </td>
                        <td className="border border-gray-100 px-2 py-2 text-center bg-gray-50/50" style={{ minWidth: "110px" }}>
                          {est?.habilitado !== false && est?.habilitado !== undefined ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold uppercase bg-green-100 text-green-700 border border-green-200">
                              <span className="h-1.5 w-1.5 rounded-full bg-green-500 animate-pulse"></span>
                              Habilitado
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold uppercase bg-red-100 text-red-700 border border-red-200">
                              <span className="h-1.5 w-1.5 rounded-full bg-red-500"></span>
                              Inhabilitado
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={1 + numColumnasAsistencia + 3} className="px-4 py-4 text-center text-gray-600 border border-gray-300">
                      No se encontraron estudiantes en Progreso.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            )}
          </div>

          <div className="text-center mt-6">
            <button
              onClick={handleGuardar}
              className="bg-green-500 text-white font-semibold py-2 px-6 rounded-lg hover:bg-green-600"
            >
              Guardar Asistencia
            </button>
          </div>
        </div>
      </ContenedorPageTemplate>
    );
  }

  export default AsistenciaPage;